// Generic offline-photo upload engine — compress, queue, upload-with-backoff,
// retry, discard. Used by BOTH the per-page hook (useOfflinePhotoQueue, while a
// record's page is open) and the app-level background flusher in AppShell (so a
// queued photo keeps uploading after the user has navigated away from that record).
// This is the single place that talks to the network for queued photos; nothing
// else should POST a queued photo directly.
import { toast } from "sonner";
import { api, formatApiError, hardTimeout } from "@/lib/api";
import {
    getQueuedPhotos, getAllQueuedPhotos, removeQueuedPhoto, updateQueuedPhotoMeta,
    getDeviceId, deviceLabel,
} from "@/lib/offlineDraft";
import { getUploadTarget } from "@/lib/photoUploadTargets";

// Compress a photo client-side BEFORE it's queued — phone photos (4-12MB) commonly
// drop to ~0.5-1MB, which is what actually makes uploads over weak site-signal succeed.
const MAX_PHOTO_EDGE = 2560;
const PHOTO_JPEG_QUALITY = 0.82;
export async function compressPhoto(file, { preserveOriginal = false } = {}) {
    try {
        // Keep the cover's source pixels for full-width PDF/Word printing.
        // Larger files still get a high-quality resize within the upload limit.
        if (preserveOriginal && file.size <= 12 * 1024 * 1024) {
            return { blob: file, filename: file.name };
        }
        if (!file.type?.startsWith("image/") || typeof window.createImageBitmap !== "function") {
            return { blob: file, filename: file.name };
        }
        const bitmap = await createImageBitmap(file);
        const longEdge = Math.max(bitmap.width, bitmap.height);
        const scale = Math.min(1, (preserveOriginal ? 4096 : MAX_PHOTO_EDGE) / longEdge);
        const quality = preserveOriginal ? 0.95 : PHOTO_JPEG_QUALITY;
        if (scale >= 1 && file.type === "image/jpeg" && !preserveOriginal) {
            bitmap.close?.();
            return { blob: file, filename: file.name };
        }
        const w = Math.max(1, Math.round(bitmap.width * scale));
        const h = Math.max(1, Math.round(bitmap.height * scale));
        let blob;
        if (typeof OffscreenCanvas !== "undefined") {
            const canvas = new OffscreenCanvas(w, h);
            canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
            blob = await canvas.convertToBlob({ type: "image/jpeg", quality });
        } else {
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
            blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
        }
        bitmap.close?.();
        if (!blob) return { blob: file, filename: file.name };
        return { blob, filename: file.name.replace(/\.\w+$/, "") + ".jpg" };
    } catch {
        return { blob: file, filename: file.name }; // decode failed — queue the original, never block the user
    }
}

export function newClientUploadId() {
    return window.crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Exponential backoff for transient failures (network blips, 5xx/408/429) — 30s, 1m, 2m,
// 5m, then every 10m indefinitely. These never become permanently "failed": a weak-signal
// site visit should keep quietly retrying for as long as it takes, not give up.
const BACKOFF_STEPS_MS = [30_000, 60_000, 120_000, 300_000];
function nextBackoffDelayMs(attempts) {
    return attempts <= BACKOFF_STEPS_MS.length ? BACKOFF_STEPS_MS[attempts - 1] : 600_000;
}
function isTransientError(e) {
    const status = e.response?.status;
    return !e.response || status === 408 || status === 429 || (status >= 500 && status < 600);
}

// Normally entityType/entityId live in q.meta (set at queue time). A handful of
// legacy queued photos predate that field being reliably stored, which made every
// retry/flush silently bail with zero network calls (getUploadTarget(undefined) ->
// no target) while the lightweight pending-photos heartbeat kept succeeding (it
// gets entityType/entityId straight from the hook's own props, not from meta) —
// giving the false impression of a healthy connection with uploads just vanishing.
// draftKey is always `photo:{entityType}:{entityId}`, so it's a reliable fallback.
function resolveEntity(meta, draftKey) {
    if (meta?.entityType && meta?.entityId) return { entityType: meta.entityType, entityId: meta.entityId };
    const m = /^photo:([^:]+):(.+)$/.exec(draftKey || "");
    return m ? { entityType: m[1], entityId: m[2] } : { entityType: meta?.entityType, entityId: meta?.entityId };
}

// axios's own `timeout` option relies on the browser's XHR timeout firing, which
// is known to never fire on Safari/WebKit when a request was in flight across a
// network interface drop (wifi off/on, sleep/wake) — the promise can hang forever,
// permanently deadlocking the single-flight flush lock below (every later retry,
// including a reconnect/"online" event, silently no-ops because the lock looks
// held). hardTimeout() (AbortController + setTimeout, from lib/api) is a
// guaranteed backstop: the browser spec requires `signal`-triggered aborts to
// settle the request's promise regardless of what the underlying transport is
// doing.
async function postQueuedPhoto(target, entityId, q) {
    const fd = target.buildFormData(q.meta, q.blob, q.meta.filename, q.meta.client_upload_id);
    const { signal, cancel } = hardTimeout(75000);
    try {
        // Do NOT set Content-Type manually for a multipart FormData body — the browser
        // must generate its own boundary, or some browsers (seen on Mac Safari) send a
        // boundary-less request that the backend can't parse, so `file` never binds and
        // the server returns 422 "Field required" even though a connection is healthy.
        return await api.post(target.uploadUrl(entityId), fd, { timeout: 60000, signal });
    } finally {
        cancel();
    }
}

async function logEvent(target, entityId, event, meta = {}) {
    if (!target.eventUrl) return;
    const { signal, cancel } = hardTimeout(15000);
    try { await api.post(target.eventUrl(entityId), { event, filename: meta.filename, error: meta.error }, { signal }); }
    catch { /* best-effort only */ }
    finally { cancel(); }
}

// Tell the server how many photos THIS device is still holding queued for this
// record, so anyone else viewing it sees a "waiting on X's phone" banner instead
// of the upload looking like data loss.
export async function reportPending(draftKey, entityType, entityId) {
    const target = getUploadTarget(entityType);
    if (!target?.pendingUrl || !navigator.onLine) return;
    const { signal, cancel } = hardTimeout(15000);
    try {
        const remaining = await getQueuedPhotos(draftKey);
        const oldest = remaining.length ? Math.min(...remaining.map((q) => q.createdAt || Date.now())) : null;
        await api.post(target.pendingUrl(entityId), {
            device_id: getDeviceId(), device_label: deviceLabel(), count: remaining.length,
            oldest_queued_at: oldest ? new Date(oldest).toISOString() : null,
        }, { signal });
    } catch { /* best-effort — never blocks the upload flow */ }
    finally { cancel(); }
}

function notifyChanged(draftKey, type, payload) {
    window.dispatchEvent(new CustomEvent("pr-photo-queue-changed", { detail: { draftKey, type, payload } }));
}

// draftKey -> { startedAt, token }. The network calls inside run() are already
// bounded by hardTimeout (~75-90s worst case), but IndexedDB itself has known
// rare hang bugs on WebKit; a lock older than STALE_LOCK_MS is treated as
// abandoned so one wedged tab can never permanently block every future retry.
// `token` guards against the abandoned attempt's own `finally` deleting the
// NEW owner's entry once it eventually (or never) settles.
const flushingKeys = new Map();
const STALE_LOCK_MS = 4 * 60 * 1000;

// Uploads every queued photo for ONE record (draftKey), oldest first. A photo NEVER
// gets silently discarded here: a real validation error (4xx, not 408/429) marks it
// "failed" (visible + manually retryable/discardable); a network error, timeout or
// transient 5xx/408/429 backs off per-photo and CONTINUES to the next photo — one
// slow/broken photo must never stop the rest of the queue uploading. Single-flight
// per draftKey, shared across every hook instance AND the app-level background
// flusher in this tab, so the same photo can never be posted twice concurrently.
export async function flushDraftKey(draftKey) {
    const existing = flushingKeys.get(draftKey);
    const stale = !!existing && Date.now() - existing.startedAt >= STALE_LOCK_MS;
    if (existing && !stale) return false;
    const token = Symbol("flush");
    flushingKeys.set(draftKey, { startedAt: Date.now(), token });
    // False once a later call has stolen this draftKey (stale-lock recovery). An
    // abandoned run keeps executing in the background after the steal; it must not
    // log health events, rewrite queue metadata or start new uploads for photos the
    // new owner is now responsible for.
    const isCurrent = () => flushingKeys.get(draftKey)?.token === token;
    let anySuccess = false;
    try {
        const run = async () => {
            const queued = await getQueuedPhotos(draftKey);
            if (!queued.length) return;
            const { entityType, entityId } = resolveEntity(queued[0].meta, draftKey);
            const target = getUploadTarget(entityType);
            if (!target) return;
            const now = Date.now();
            for (const q of queued) {
                if (!isCurrent()) return; // superseded — the new owner handles the rest of the queue
                if (q.meta.failed) continue; // permanent failure — only a manual retry/discard now
                if (q.meta.nextRetryAt && q.meta.nextRetryAt > now) continue; // still backing off
                try {
                    const resp = await postQueuedPhoto(target, entityId, q);
                    await removeQueuedPhoto(q.id);
                    anySuccess = true;
                    notifyChanged(draftKey, "uploaded", resp?.data);
                } catch (e) {
                    if (!isCurrent()) return; // abandoned attempt timing out — not a real offline event
                    const attempts = (q.meta.attempts || 0) + 1;
                    if (isTransientError(e)) {
                        const nextRetryAt = Date.now() + nextBackoffDelayMs(attempts);
                        const error = e.response ? (formatApiError(e.response?.data?.detail) || "Upload failed") : "Network error — will keep retrying";
                        if (!q.meta.offline_logged) logEvent(target, entityId, "offline_retry", { filename: q.meta.filename, error });
                        await updateQueuedPhotoMeta(q.id, { attempts, nextRetryAt, error, failed: false, offline_logged: true });
                        notifyChanged(draftKey, "retry_scheduled");
                        // never break — the rest of the queue still deserves its turn this sweep
                    } else {
                        const error = formatApiError(e.response?.data?.detail) || "Upload failed";
                        logEvent(target, entityId, "failed", { filename: q.meta.filename, error });
                        await updateQueuedPhotoMeta(q.id, { attempts, failed: true, error });
                        toast.error(`Photo failed to upload: ${q.meta.filename || "photo"} — ${error}. It's kept on this device, tap it to retry or discard.`, { duration: 15000 });
                        notifyChanged(draftKey, "failed");
                    }
                }
            }
            if (isCurrent()) await reportPending(draftKey, entityType, entityId);
        };
        // After a steal, the abandoned run() is detached from its (already-rejected)
        // lock request, so any error it throws later would be an unhandled rejection
        // (full-screen overlay in dev). Only the current owner's errors propagate.
        const guardedRun = async () => {
            try { await run(); }
            catch (e) { if (isCurrent()) throw e; }
        };
        if (navigator.locks?.request) {
            try {
                if (stale) {
                    // The previous holder's Web Lock callback never released on its own
                    // (an IndexedDB hang network-level hardTimeout can't reach) — forcibly
                    // take the lock so this tab can recover without needing a full reload.
                    await navigator.locks.request(`pr-photo-flush-${draftKey}`, { steal: true }, async () => { await guardedRun(); });
                } else {
                    await navigator.locks.request(`pr-photo-flush-${draftKey}`, { ifAvailable: true }, async (lock) => { if (lock) await guardedRun(); });
                }
            } catch (e) {
                // The OLD, hung holder's own request rejects with AbortError once a
                // later call steals its lock out from under it — expected, not a real
                // failure; swallow it so it never surfaces as an unhandled rejection
                // (which would otherwise also abort flushAllQueuedPhotos' for-loop).
                if (e?.name !== "AbortError") throw e;
            }
        } else {
            await guardedRun();
        }
    } finally {
        if (flushingKeys.get(draftKey)?.token === token) flushingKeys.delete(draftKey);
    }

    return anySuccess;
}

// Manual retry for a single queued photo (offered on a failed thumbnail) — clears
// the permanent-failure flag and backoff timer so it gets a fresh attempt right away.
export async function retryOneQueuedPhoto(draftKey, tempId) {
    const queued = await getQueuedPhotos(draftKey);
    const q = queued.find((x) => x.id === tempId);
    if (!q) return { ok: false };
    const { entityType, entityId } = resolveEntity(q.meta, draftKey);
    const target = getUploadTarget(entityType);
    if (!target) return { ok: false, error: "This photo can't be identified (older queue format) — please discard and re-add it" };
    await updateQueuedPhotoMeta(tempId, { failed: false, nextRetryAt: null });
    try {
        const resp = await postQueuedPhoto(target, entityId, q);
        await removeQueuedPhoto(q.id);
        await reportPending(draftKey, entityType, entityId);
        notifyChanged(draftKey, "uploaded", resp?.data);
        return { ok: true };
    } catch (e) {
        const error = e.response ? (formatApiError(e.response?.data?.detail) || "Upload failed") : "Still offline";
        const hardFail = e.response ? !isTransientError(e) : false;
        if (hardFail) logEvent(target, entityId, "failed", { filename: q.meta.filename, error });
        else if (!q.meta.offline_logged) logEvent(target, entityId, "offline_retry", { filename: q.meta.filename, error });
        await updateQueuedPhotoMeta(q.id, { attempts: (q.meta.attempts || 0) + 1, failed: hardFail, error, offline_logged: true });
        notifyChanged(draftKey, "failed");
        return { ok: false, error };
    }
}

// Discard a queued/failed photo from this device entirely (e.g. the wrong photo was
// taken) — removes it from IndexedDB and updates the server of the new (lower)
// pending count so the "waiting on this device" banner clears for other viewers.
export async function discardOneQueuedPhoto(draftKey, tempId) {
    const queued = await getQueuedPhotos(draftKey);
    const q = queued.find((x) => x.id === tempId);
    await removeQueuedPhoto(tempId);
    if (q) { const { entityType, entityId } = resolveEntity(q.meta, draftKey); await reportPending(draftKey, entityType, entityId); }
    notifyChanged(draftKey, "discarded");
}

// Discard every photo still queued for one record on this device.
export async function discardAllQueuedPhotos(draftKey) {
    const queued = await getQueuedPhotos(draftKey);
    for (const q of queued) await removeQueuedPhoto(q.id);
    if (queued.length) { const { entityType, entityId } = resolveEntity(queued[0].meta, draftKey); await reportPending(draftKey, entityType, entityId); }
    notifyChanged(draftKey, "discarded");
}

// Lets the record's owner/an admin clear a STALE "waiting on another device" entry
// (e.g. the device was lost/reset and will never come back online).
export async function clearOtherDevicePending(entityType, entityId, deviceId) {
    const target = getUploadTarget(entityType);
    if (!target?.pendingUrl) return;
    await api.post(target.pendingUrl(entityId), { device_id: deviceId, device_label: "", count: 0 });
}

// App-level sweep — flushes EVERY record queued on this device, regardless of
// whether that record's page is currently open. Used by AppShell's background
// flusher so photos keep uploading even after the user navigates away.
export async function flushAllQueuedPhotos() {
    const all = await getAllQueuedPhotos();
    const keys = [...new Set(all.map((q) => q.draftKey))];
    for (const key of keys) await flushDraftKey(key);
}
